import { Component, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LoadingEntity, ModalService, PageChange, PageData } from '@inst-iot/bosch-angular-ui-components';
import { Vehicle, VehicleCurrentInfo } from 'src/app/_shared/models/vehicle';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { NavigationService } from 'src/app/_services/navigation.service';
import { CompanyService } from 'src/app/_services/company.service';
import { environment } from 'src/environments/environment';
import { CertificateService } from 'src/app/_services/certificate.service';
import { UserService } from 'src/app/_services/user.service';
import { KmToMilesPipe } from 'src/app/_shared/pipes/km-to-miles.pipe';
import { RemoveDecimalPointsPipe } from 'src/app/_shared/pipes/remove-decimal-points.pipe';
import { InTimePipe } from 'src/app/_shared/pipes/in-time.pipe';
import { KWhKmToMilesPipe } from 'src/app/_shared/pipes/k-wh-km-to-miles.pipe';
import { sortRbTableData } from 'src/app/_shared/utils/sort-utils';
import * as pdfMake from 'pdfmake/build/pdfmake';

declare const require: any;
const pdfFontsVFS = require('pdfmake/build/vfs_fonts.js');

@Component({
  selector: 'app-vehicles',
  templateUrl: './vehicles.component.html',
  styleUrls: ['./vehicles.component.scss']
})
export class VehiclesComponent implements OnInit, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];
  @Input() fleetName: string = '';
  // Filters
  vehicleBrandOptions: any[] = [];
  selectedVehicleBrandOptions: any;
  vehicleBrandArray: string[] = [];
  vehicleTypeOptions: any[] = [];
  selectedVehicleTypeOptions: any;
  vehicleTypeArray: string[] = [];
  productionYearOptions: any[] = [];
  selectedProductionYearOptions: any;
  productionYearArray: string[] = [];
  // Pagination
  limit = 21;
  pageData = new PageData({ number: 0, size: this.limit, totalElements: 0, numberOfElements: 0 });
  // Views
  cardView = true;
  listView = false;
  dataLoading = false;
  errorMessage = false;
  lastView = '';
  loader = new LoadingEntity();
  // Unified vehicle data
  rawVehicles: Vehicle[] = [];
  displayVehicles: any[] = [];
  vehiclesInfo: { [key: string]: VehicleCurrentInfo } = {};
  certificatesStatus: { [key: string]: any } = {};
  // full list used for building fixed filter options
  filterVehiclesCache: Vehicle[] = [];
  isPrefetchingAllVehicles = false;
  // certificate
  buttonLabels = {
    confirmLabel: 'Download',
    cancelLabel: 'Cancel',
    optionalLabel: 'Optional'
  };
  // Search
  searchQuery = '';
  // whether we're currently using the client-side search fallback
  private clientSearchActive = false;
  // MyGeotab user and company info
  companyData: any;
  isMetric: boolean | undefined;
  // Sorting
  sortField: string = '';
  sortDirection: '' | 'asc' | 'desc' = '';
  readonly sortData = sortRbTableData;
  // Pipes
  private readonly kmToMilesPipe = new KmToMilesPipe();
  private readonly removeDecimalPointsPipe = new RemoveDecimalPointsPipe();
  private readonly inTimePipe = new InTimePipe();
  private readonly kWhKmToMilesPipe = new KWhKmToMilesPipe();

  constructor(
    private readonly modalService: ModalService,
    private readonly navigationService: NavigationService,
    private readonly vehicleInfoService: VehicleInfoService,
    private readonly certificateService: CertificateService,
    private readonly companyService: CompanyService,
    private readonly userService: UserService
  ) {}

  ngOnDestroy(): void {
    window.removeEventListener('hashchange', this.onHashChange.bind(this));
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    const credentials = sessionStorage.getItem(`credentials_${environment.appId}`);
    if (credentials) {
      const parsed = JSON.parse(credentials);
      this.companyService.getCompanyData(parsed.database).pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: c => this.companyData = c,
        error: e => console.error('Error fetching company:', e)
      });
      this.userService.getUser(parsed.userName, parsed.database).pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: u => this.isMetric = u?.isMetric ?? null,
        error: e => console.error('Error fetching user:', e)
      });
    }

    this.loadFilterOptions();
    this.loadVehiclesPage(1);

    window.addEventListener('hashchange', this.onHashChange.bind(this));
    this.onHashChange();
  }

  private onHashChange() {
    const fragment = window.location.hash.substring(1);
    console.log('Fragment changed:', fragment);
    if (typeof (window as any).geotab !== 'undefined' && (window as any).geotab.addin) {
      const assign = window.location.href.includes('dev') ? 'bitcDev' : 'bitc';
      (window as any).geotab.addin[assign] = (event: any, api: any, state: any) => {
        const groups = state.getGroupFilter() || [];
        if (groups.length > 0) {
          this.displayVehicles = this.displayVehicles.filter((vehicle: any) =>
            Array.isArray(vehicle.deviceGroup) && vehicle.deviceGroup.some((g: string) => groups.includes(g))
          );
        } else {
          this.addCurrentDataToVehicles();
        }
      };
    }
  }

  goTo(page: string) { this.navigationService.navigateTo(page); }
  goToCardView() { this.cardView = true; this.listView = false; }
  goToListView() { this.cardView = false; this.listView = true; }
  goToInsights(id: string) { this.navigationService.navigateTo('insights', id); }

  openDialog(tpl: TemplateRef<any>, deviceSerialNumber: string) {
    this.modalService.open(tpl, { maxWidth: '650px' }).then(close => {
      if (close) {
        this.certificateService.getCertificate(deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: data => window.open(this.cleanUrl(data.url)),
          error: err => console.error('Error downloading certificate:', err)
        });
      }
    });
  }

  cleanUrl(rawUrl: string): string {
    if (!rawUrl) return '';
    let url = rawUrl.split('\\"').join('').trim();
    let start = 0;
    while (start < url.length && url[start] === '"') {
      start++;
    }
    let end = url.length;
    while (end > start && url[end - 1] === '"') {
      end--;
    }
    url = url.slice(start, end).trim();
    const httpIndex = url.indexOf('http');
    url = httpIndex !== -1 ? url.substring(httpIndex) : url;
    end = url.length;
    while (end > 0) {
      const character = url[end - 1];
      if (character !== '"' && character !== "'" && character !== '\\' && character.trim() !== '') break;
      end--;
    }
    return url.slice(0, end);
  }

  navigateCall(pageChange: PageChange) {
    this.pageData = new PageData({ ...this.pageData, number: pageChange.number });
    // If global sort or client-side search is active, paginate client-side to preserve ordering
    if (this.sortField || this.clientSearchActive) {
      // ensure cache available
      if (!this.filterVehiclesCache || this.filterVehiclesCache.length === 0) {
        this.dataLoading = true;
        this.vehicleInfoService.getVehiclesWithPagination(1, 0, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: res => {
            this.filterVehiclesCache = res.data || [];
            this.applyClientSideSortAndPaginate();
          },
          error: err => {
            console.error('Error prefetching full vehicles for pagination:', err);
            this.dataLoading = false;
            // fallback to server load
            this.loadVehiclesPage(this.pageData.number + 1);
          }
        });
      } else {
        this.applyClientSideSortAndPaginate();
      }
      return;
    }
    this.loadVehiclesPage(this.pageData.number + 1);
  }

  private loadVehiclesPage(page: number) {
    this.dataLoading = true;
    this.errorMessage = false;
    // ensure filters are up-to-date before deciding server/client paths
    this.checkFilters();
    // If there's an active search query, perform client-side search over the full cached dataset
    const q = (this.searchQuery || '').toLowerCase().trim();
    if (q !== '') {
      this.clientSearchActive = true;
      // ensure we have the full list to search on
      const performSearch = (source?: Vehicle[]) => {
        const full = source || this.filterVehiclesCache || [];
        const filtered = full.filter(v =>
          (v.name || '').toString().toLowerCase().includes(q) ||
          (v.batteryIdentifier || '').toString().toLowerCase().includes(q) ||
          (v.vehicleModel || '').toString().toLowerCase().includes(q) ||
          (v.vehicleBrand || '').toString().toLowerCase().includes(q)
        );
        const total = filtered.length;
        const pageSize = this.pageData.size || this.limit;
        const start = (page - 1) * pageSize;
        const pageSlice = filtered.slice(start, start + pageSize);
        this.pageData = new PageData({ number: page - 1, size: pageSize, totalElements: total, numberOfElements: pageSlice.length });
        const ids = pageSlice.map((v: any) => v.batteryIdentifier).filter(Boolean);
        if (ids.length > 0) {
          this.vehicleInfoService.getCurrentVehicleInfo(ids).pipe(takeUntil(this.unsubscribe$)).subscribe({
            next: info => {
              // merge the info for items on this page
              this.vehiclesInfo = { ...this.vehiclesInfo, ...info };
              this.displayVehicles = pageSlice.map((v: any) => ({ ...v, ...(this.vehiclesInfo[v.batteryIdentifier] || {}) }));
              this.addCertificatesStatusToVehicleCard(ids);
              this.dataLoading = false;
              this.updateErrorMessage();
            },
            error: e => {
              console.error('Error fetching Current Vehicles info for search page:', e);
              this.displayVehicles = pageSlice;
              this.dataLoading = false;
              this.updateErrorMessage();
            }
          });
        } else {
          this.displayVehicles = [];
          this.dataLoading = false;
          this.updateErrorMessage();
        }
      };

      const localHasFilters = this.vehicleBrandArray.length > 0 || this.vehicleTypeArray.length > 0 || this.productionYearArray.length > 0;
      if (localHasFilters) {
        // when filters are active, fetch the full filtered list from server (limit=0) then search client-side
        this.vehicleInfoService.getFilteredVehicles(1, 0, this.fleetId, this.vehicleBrandArray, this.vehicleTypeArray, this.productionYearArray).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: res => {
            const searchSource = res.data || [];
            // do NOT overwrite this.filterVehiclesCache here: keep dropdown options stable (use full cache)
            performSearch(searchSource);
          },
          error: err => {
            console.error('Error loading filtered vehicles for search:', err);
            this.dataLoading = false;
          }
        });
      } else if (!this.filterVehiclesCache || this.filterVehiclesCache.length === 0) {
        // load full list first (no filters)
        this.vehicleInfoService.getVehiclesWithPagination(1, 0, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: res => {
            this.filterVehiclesCache = res.data || [];
            this.getVehicleBrandOptionsList();
            this.getVehicleTypeOptionsList();
            this.getProductionYearOptionsList();
            performSearch();
          },
          error: err => {
            console.error('Error loading full vehicles for search:', err);
            this.dataLoading = false;
          }
        });
      } else {
        performSearch();
      }
      return;
    }
    
    // no search -> disable client search mode
    this.clientSearchActive = false;
    const pageSize = this.pageData.size || this.limit;
    const hasFilters = this.vehicleBrandArray.length > 0 || this.vehicleTypeArray.length > 0 || this.productionYearArray.length > 0;
    const obs = hasFilters
      ? this.vehicleInfoService.getFilteredVehicles(page, pageSize, this.fleetId, this.vehicleBrandArray, this.vehicleTypeArray, this.productionYearArray)
      : this.vehicleInfoService.getVehiclesWithPagination(page, pageSize, this.fleetId);

    obs.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: res => {
        this.rawVehicles = res.data || [];
        this.pageData = new PageData({ number: page - 1, size: pageSize, totalElements: res.total ?? 0, numberOfElements: (res.data || []).length });
      },
      error: err => {
        console.error('Error fetching Vehicles data:', err);
        // clear display items on error and update error flag consistently
        this.displayVehicles = [];
        this.pageData = new PageData({ ...this.pageData, numberOfElements: 0 });
        this.dataLoading = false;
        this.updateErrorMessage();
      },
      complete: () => {
        const ids = this.rawVehicles.map(v => v.batteryIdentifier).filter(Boolean);
        if (ids.length > 0) {
          const missing = ids.filter(id => !this.vehiclesInfo || !this.vehiclesInfo[id]);
          if (missing.length > 0) {
            this.vehicleInfoService.getCurrentVehicleInfo(missing).pipe(takeUntil(this.unsubscribe$)).subscribe({
              next: info => { this.vehiclesInfo = { ...this.vehiclesInfo, ...info }; this.addCurrentDataToVehicles(); this.addCertificatesStatusToVehicleCard(missing); },
              error: e => { console.error('Error fetching Current Vehicles info:', e); this.addCurrentDataToVehicles(); }
            });
          } else {
            // we already have current info for these items
            this.addCurrentDataToVehicles();
            // ensure certificate status is present for any missing certs
            const missingCerts = ids.filter(id => !this.certificatesStatus || !this.certificatesStatus[id]);
            if (missingCerts.length > 0) this.addCertificatesStatusToVehicleCard(missingCerts);
          }
        } else {
          this.addCurrentDataToVehicles();
        }
      }
    });
  }

  private updateErrorMessage() {
    // error should be shown when not loading and no display items exist
    this.errorMessage = !this.dataLoading && (!this.displayVehicles || this.displayVehicles.length === 0);
  }

  private loadFilterOptions() {
    // Prefetch all pages to build fixed filter option lists (robust fallback)
    this.retrieveAllVehiclesPages();
  }

  // Fetch all pages and store them in `filterVehiclesCache` (used for global search/sort)
  retrieveAllVehiclesPages() {
    if (this.isPrefetchingAllVehicles) return;
    this.isPrefetchingAllVehicles = true;
    this.vehicleInfoService.getVehiclesWithPagination(1, this.limit, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: firstResp => {
        const total = firstResp.total || (firstResp.data ? firstResp.data.length : 0);
        this.filterVehiclesCache = firstResp.data || [];
        const pages = Math.ceil(total / this.limit);
        const requests: any[] = [];
        for (let p = 2; p <= pages; p++) {
          requests.push(this.vehicleInfoService.getVehiclesWithPagination(p, this.limit, this.fleetId));
        }
        // always fetch current vehicle info for the cached set after we assemble it
        const finishPopulate = (items: Vehicle[]) => {
          this.filterVehiclesCache = items;
          this.isPrefetchingAllVehicles = false;
          this.getVehicleBrandOptionsList();
          this.getVehicleTypeOptionsList();
          this.getProductionYearOptionsList();
          this.fetchCurrentInfoForCache();
        };

        if (requests.length === 0) {
          finishPopulate(firstResp.data || []);
          return;
        }
        forkJoin(requests).pipe(takeUntil(this.unsubscribe$)).subscribe(resArray => {
          const rest = resArray.flatMap((r: any) => r.data || []);
          finishPopulate((firstResp.data || []).concat(rest));
        }, err => {
          console.error('Error fetching additional pages for prefetch', err);
          this.isPrefetchingAllVehicles = false;
          // fallback: still build options from what we have
          this.getVehicleBrandOptionsList();
          this.getVehicleTypeOptionsList();
          this.getProductionYearOptionsList();
          // try to fetch current info for what we have
          this.fetchCurrentInfoForCache();
        });
      },
      error: err => {
        console.error('Error fetching first page for prefetch', err);
        this.isPrefetchingAllVehicles = false;
      }
    });
  }

  private fetchCurrentInfoForCache() {
    const ids = (this.filterVehiclesCache || []).map(v => v.batteryIdentifier).filter(Boolean);
    if (!ids || ids.length === 0) return;
    // use larger chunks to reduce the total number of requests for large fleets
    const chunkSize = Math.max(this.limit, 100); // at least page-size, prefer batches of 100
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += chunkSize) {
      chunks.push(ids.slice(i, i + chunkSize));
    }
    const requests = chunks.map(c => this.vehicleInfoService.getCurrentVehicleInfo(c));
    if (requests.length === 0) return;
    forkJoin(requests).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: arr => {
        try {
          const merged = arr.reduce((acc: any, cur: any) => ({ ...acc, ...cur }), {});
          this.vehiclesInfo = { ...this.vehiclesInfo, ...merged };
          // also populate certificates status for cached items
          this.addCertificatesStatusToVehicleCard(ids);
          // if a global sort is active, re-apply it now that we have more current info
          if (this.sortField) {
            // keep page at first position after sort
            this.pageData = new PageData({ ...this.pageData, number: 0 });
            this.applyClientSideSortAndPaginate();
          }
        } catch (e) {
          console.error('Error merging current vehicle info for cache', e);
        }
      },
      error: e => console.error('Error fetching current vehicle info for cached vehicles', e)
    });
  }

  private addCurrentDataToVehicles() {
    this.displayVehicles = this.rawVehicles.map(v => ({ ...v, ...(this.vehiclesInfo ? this.vehiclesInfo[v.batteryIdentifier] : {}) }));
    this.applySearchAndFilters();
    // after applying search and filters, set errorMessage based on result set
    this.updateErrorMessage();
    this.dataLoading = false;
  }

  addCertificatesStatusToVehicleCard(deviceSerialNumber: string[]) {
    if (!deviceSerialNumber || deviceSerialNumber.length === 0) return;
    // split into batches to avoid long URI errors (HTTP 414)
    const batchSize = 50;
    const chunks: string[][] = [];
    for (let i = 0; i < deviceSerialNumber.length; i += batchSize) {
      chunks.push(deviceSerialNumber.slice(i, i + batchSize));
    }
    const requests = chunks.map(c => this.certificateService.getCertificateStatus(c));
    if (requests.length === 0) return;
    forkJoin(requests).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (responses: any[]) => {
        // merge all responses into the certificatesStatus map
        responses.forEach(resp => {
          if (resp && typeof resp === 'object') {
            Object.keys(resp).forEach(k => this.certificatesStatus[k] = resp[k]);
          }
        });
        // update displayed vehicles with any found certificate statuses
        this.displayVehicles.forEach(v => {
          const cert = this.certificatesStatus[v.batteryIdentifier];
          if (cert) v.certificateStatus = cert;
        });
      },
      error: err => console.error('Error fetching Certificate Status data (batched):', err)
    });
  }

  applySearchAndFilters() {
    const q = (this.searchQuery || '').toLowerCase().trim();
    if (q !== '') {
      // ensure filters reflect current selections
      this.checkFilters();
      // switch to client-side paged search: reset to first page and load via loadVehiclesPage
      this.pageData = new PageData({ number: 0, size: this.limit, totalElements: 0, numberOfElements: 0 });
      this.loadVehiclesPage(1);
      return;
    }

    // If we were previously using client-side search, and now the search is cleared,
    // reload server paginated data so totals and pages are correct.
    if (this.clientSearchActive) {
      this.clientSearchActive = false;
      this.pageData = new PageData({ number: 0, size: this.limit, totalElements: 0, numberOfElements: 0 });
      this.loadVehiclesPage(1);
      return;
    }

    this.displayVehicles = this.rawVehicles.map(v => ({ ...v, ...(this.vehiclesInfo ? this.vehiclesInfo[v.batteryIdentifier] : {}) }))
      .filter(v => {
        const matchesBrand = this.vehicleBrandArray.length === 0 || (v.vehicleBrand && this.vehicleBrandArray.includes(v.vehicleBrand));
        const matchesType = this.vehicleTypeArray.length === 0 || (v.vehicleType && this.vehicleTypeArray.includes(v.vehicleType));
        const matchesYear = this.productionYearArray.length === 0 || (v.productionYear && this.productionYearArray.includes(v.productionYear.toString()));
        return matchesBrand && matchesType && matchesYear;
      });
  }

  checkFilters() {
    this.vehicleBrandArray = [];
    this.vehicleTypeArray = [];
    this.productionYearArray = [];

    // The multi-select emits a map of selected ids ({ id: true }), use that shape
    if (this.selectedVehicleBrandOptions) this.vehicleBrandArray = Object.keys(this.selectedVehicleBrandOptions).filter(k => this.selectedVehicleBrandOptions[k] === true);
    if (this.selectedVehicleTypeOptions) this.vehicleTypeArray = Object.keys(this.selectedVehicleTypeOptions).filter(k => this.selectedVehicleTypeOptions[k] === true);
    if (this.selectedProductionYearOptions) this.productionYearArray = Object.keys(this.selectedProductionYearOptions).filter(k => this.selectedProductionYearOptions[k] === true);
  
  }

  getVehiclesFiltered() {
    this.checkFilters();
    this.loadVehiclesPage(1);
  }

  onSortChange(event: { id: string, direction: string }) {
    this.sortField = event?.id || '';
    this.sortDirection = (event?.direction as '' | 'asc' | 'desc') || '';
    this.pageData = new PageData({ ...this.pageData, number: 0 });
    // ensure we have full cache for global sort
    if (!this.filterVehiclesCache || this.filterVehiclesCache.length === 0) {
      this.vehicleInfoService.getVehiclesWithPagination(1, 0, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: res => {
          this.filterVehiclesCache = res.data || [];
          this.applyClientSideSortAndPaginate();
        },
        error: err => {
          console.error('Error prefetching full vehicles for sort:', err);
        }
      });
    } else {
      this.applyClientSideSortAndPaginate();
    }
  }

  private applyClientSideSortAndPaginate() {
    const q = (this.searchQuery || '').toLowerCase().trim();
    // ensure filters arrays are current
    this.checkFilters();
    let list = (this.filterVehiclesCache || []).slice();
    // apply filters
    list = list.filter(v => {
      const matchesBrand = this.vehicleBrandArray.length === 0 || (v.vehicleBrand && this.vehicleBrandArray.includes(v.vehicleBrand));
      const matchesType = this.vehicleTypeArray.length === 0 || (v.vehicleType && this.vehicleTypeArray.includes(v.vehicleType));
      const matchesYear = this.productionYearArray.length === 0 || (v.productionYear && this.productionYearArray.includes(v.productionYear.toString()));
      return matchesBrand && matchesType && matchesYear;
    });
    // apply search
    if (q) {
      list = list.filter(v => {
        return [v.name, v.vehicleModel, v.vehicleBrand, v.batteryIdentifier]
          .some((f: any) => (f || '').toString().toLowerCase().includes(q));
      });
    }
    // apply sort
    if (this.sortField) {
      // merge any already-fetched current info into list items so sort can use numeric fields
      list = list.map((v: any) => ({ ...v, ...(this.vehiclesInfo && v.batteryIdentifier ? (this.vehiclesInfo[v.batteryIdentifier] || {}) : {}) }));
      list = this.sortData({ id: this.sortField, direction: this.sortDirection }, list);
    }
    const total = list.length;
    const pageSize = this.pageData.size || this.limit;
    const start = (this.pageData.number || 0) * pageSize;
    const pageSlice = list.slice(start, start + pageSize);
    this.pageData = new PageData({ number: this.pageData.number, size: pageSize, totalElements: total, numberOfElements: pageSlice.length });
    const ids = pageSlice.map((v: any) => v.batteryIdentifier).filter(Boolean);
    if (ids.length > 0) {
      const missing = ids.filter(id => !this.vehiclesInfo || !this.vehiclesInfo[id]);
      if (missing.length > 0) {
        this.vehicleInfoService.getCurrentVehicleInfo(missing).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: info => {
            this.vehiclesInfo = { ...this.vehiclesInfo, ...info };
            this.displayVehicles = pageSlice.map((v: any) => ({ ...v, ...(this.vehiclesInfo[v.batteryIdentifier] || {}) }));
            this.addCertificatesStatusToVehicleCard(missing);
            this.updateErrorMessage();
            this.dataLoading = false;
          },
          error: e => {
            console.error('Error fetching current vehicle info for sorted page:', e);
            this.displayVehicles = pageSlice;
            this.updateErrorMessage();
            this.dataLoading = false;
          }
        });
      } else {
        this.displayVehicles = pageSlice.map((v: any) => ({ ...v, ...(this.vehiclesInfo[v.batteryIdentifier] || {}) }));
        const missingCerts = ids.filter(id => !this.certificatesStatus || !this.certificatesStatus[id]);
        if (missingCerts.length > 0) this.addCertificatesStatusToVehicleCard(missingCerts);
        this.updateErrorMessage();
        this.dataLoading = false;
      }
    } else {
      this.displayVehicles = [];
      this.updateErrorMessage();
      this.dataLoading = false;
    }
  }

  getVehicleBrandOptionsList() {
    const set = new Set<string>();
    // combine cached full-list and current raw page to avoid transient empty options
    (this.filterVehiclesCache || []).forEach(i => { if (i.vehicleBrand) set.add(i.vehicleBrand); });
    (this.rawVehicles || []).forEach(i => { if (i.vehicleBrand) set.add(i.vehicleBrand); });
    // also include currently selected brands to avoid disappearing selections
    if (this.selectedVehicleBrandOptions) {
      if (Array.isArray(this.selectedVehicleBrandOptions)) {
        this.selectedVehicleBrandOptions.forEach((s: any) => { if (s && s.id) set.add(s.id); });
      } else {
        Object.keys(this.selectedVehicleBrandOptions).forEach(k => { if (this.selectedVehicleBrandOptions[k]) set.add(k); });
      }
    }
    this.vehicleBrandOptions = Array.from(set).map(x => ({ id: x, label: x }));
  }

  getVehicleTypeOptionsList() {
    const set = new Set<string>();
    (this.filterVehiclesCache || []).forEach(i => { if (i.vehicleType) set.add(i.vehicleType); });
    (this.rawVehicles || []).forEach(i => { if (i.vehicleType) set.add(i.vehicleType); });
    if (this.selectedVehicleTypeOptions) {
      if (Array.isArray(this.selectedVehicleTypeOptions)) {
        this.selectedVehicleTypeOptions.forEach((s: any) => { if (s && s.id) set.add(s.id); });
      } else {
        Object.keys(this.selectedVehicleTypeOptions).forEach(k => { if (this.selectedVehicleTypeOptions[k]) set.add(k); });
      }
    }
    this.vehicleTypeOptions = Array.from(set).map(x => ({ id: x, label: x }));
  }

  getProductionYearOptionsList() {
    const set = new Set<string>();
    (this.filterVehiclesCache || []).forEach(i => { if (i.productionYear) set.add(i.productionYear.toString()); });
    (this.rawVehicles || []).forEach(i => { if (i.productionYear) set.add(i.productionYear.toString()); });
    if (this.selectedProductionYearOptions) {
      if (Array.isArray(this.selectedProductionYearOptions)) {
        this.selectedProductionYearOptions.forEach((s: any) => { if (s && s.id) set.add(s.id.toString()); });
      } else {
        Object.keys(this.selectedProductionYearOptions).forEach(k => { if (this.selectedProductionYearOptions[k]) set.add(k.toString()); });
      }
    }
    this.productionYearOptions = Array.from(set).map(x => ({ id: x, label: x }));
  }

  getFormattedMileage(mileage: number | null | undefined): string {
    if (mileage === null || mileage === undefined) return 'N/A';
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? `${this.removeDecimalPointsPipe.transform(mileage, 0)} km` : `${this.removeDecimalPointsPipe.transform(this.kmToMilesPipe.transform(mileage), 0)} mi`;
    } else {
      if (this.companyData?.companyAddress?.country === 'United States (US)') return `${this.removeDecimalPointsPipe.transform(this.kmToMilesPipe.transform(mileage), 0)} mi`;
      return `${this.removeDecimalPointsPipe.transform(mileage, 0)} km`;
    }
  }

  getFormattedAvgEfficiency(avgEvEfficiency: number | null | undefined): string {
    if (avgEvEfficiency === null || avgEvEfficiency === undefined) return 'N/A';
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? `${this.removeDecimalPointsPipe.transform(avgEvEfficiency, 0)} kWh/100km` : `${this.removeDecimalPointsPipe.transform(this.kWhKmToMilesPipe.transform(avgEvEfficiency), 1)} mi/kWh`;
    } else {
      if (this.companyData?.companyAddress?.country === 'United States (US)') return `${this.removeDecimalPointsPipe.transform(this.kWhKmToMilesPipe.transform(avgEvEfficiency), 1)} mi/kWh`;
      return `${this.removeDecimalPointsPipe.transform(avgEvEfficiency, 0)} kWh/100km`;
    }
  }

  getFormattedPredRange(predRange: number | null | undefined, predictionDays: number | null | undefined): string {
    if (predRange === null || predRange === undefined) return 'N/A';
    const daysStr = (predictionDays !== null && predictionDays !== undefined) ? ` (${this.inTimePipe.transform(predictionDays, false)})` : '';
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? `${this.removeDecimalPointsPipe.transform(predRange, 0)} km${daysStr}` : `${this.removeDecimalPointsPipe.transform(this.kmToMilesPipe.transform(predRange), 0)} mi${daysStr}`;
    } else {
      if (this.companyData?.companyAddress?.country === 'United States (US)') return `${this.removeDecimalPointsPipe.transform(this.kmToMilesPipe.transform(predRange), 0)} mi${daysStr}`;
      return `${this.removeDecimalPointsPipe.transform(predRange, 0)} km${daysStr}`;
    }
  }

  async downloadTablePdfReport(): Promise<void> {
    if (!this.displayVehicles || this.displayVehicles.length === 0) return;

    const reportDate = new Date();
    const dateLabel = reportDate.toLocaleDateString('it-IT');
    const fleetNameLabel = (this.fleetName || '').trim() || 'N/A';

    const fonts = {
      Roboto: {
        normal: 'Roboto-Regular.ttf',
        bold: 'Roboto-Bold.ttf',
        italics: 'Roboto-Italic.ttf',
        bolditalics: 'Roboto-BoldItalic.ttf'
      }
    };

    const tableHeader = [
      'Asset Name',
      'Vehicle Brand',
      'Vehicle Model',
      'Mileage',
      'State of Health',
      'Predicted State of Health',
      'Range from Efficiency',
      'Predicted Range from Efficiency',
      'Avg. Efficiency'
    ];

    const tableBody = this.displayVehicles.map(v => {
      const predictionDaysLabel = v.predictionDays
        ? this.inTimePipe.transform(v.predictionDays, false)
        : 'in 1 year';
      const predictedSoh = v.predSohc
        ? `${this.removeDecimalPointsPipe.transform(v.predSohc, 0)}% (${predictionDaysLabel})`
        : 'N/A';

      return [
        v.name || 'N/A',
        v.vehicleBrand || 'N/A',
        v.vehicleModel || 'N/A',
        this.getFormattedMileage(v.mileage),
        v.batteryStateOfHealth ? `${this.removeDecimalPointsPipe.transform(v.batteryStateOfHealth, 0)}%` : 'N/A',
        predictedSoh,
        this.getFormattedMileage(v.currRange),
        this.getFormattedPredRange(v.predRange, v.predictionDays),
        this.getFormattedAvgEfficiency(v.avgEvEfficiency)
      ];
    });

    const docDefinition: any = {
      pageOrientation: 'landscape',
      content: [
        { text: 'Vehicles Report', style: 'header' },
        { text: `Fleet: ${fleetNameLabel} | Date: ${dateLabel}`, style: 'subheader' },
        {
          style: 'tableExample',
          table: { headerRows: 1, body: [tableHeader, ...tableBody] },
          layout: 'lightHorizontalLines'
        }
      ],
      styles: {
        header: {
          fontSize: 16,
          bold: true,
          margin: [0, 0, 0, 10]
        },
        subheader: {
          fontSize: 11,
          margin: [0, 0, 0, 8]
        }
      },
      defaultStyle: {
        font: 'Roboto',
        fontSize: 10
      },
      fonts,
      vfs: pdfFontsVFS
    };

    const timestamp = reportDate.toISOString().slice(0, 10);
    const normalizedFleetName = fleetNameLabel.toLowerCase();
    let fleetNameForFile = '';
    for (const character of normalizedFleetName) {
      const isLowercaseLetter = character >= 'a' && character <= 'z';
      const isDigit = character >= '0' && character <= '9';
      if (isLowercaseLetter || isDigit) {
        fleetNameForFile += character;
      } else if (fleetNameForFile && !fleetNameForFile.endsWith('-')) {
        fleetNameForFile += '-';
      }
    }
    while (fleetNameForFile.endsWith('-')) {
      fleetNameForFile = fleetNameForFile.slice(0, -1);
    }
    fleetNameForFile = fleetNameForFile || 'fleet';
    pdfMake.createPdf(docDefinition).download(`vehicles-report-${fleetNameForFile}-${timestamp}.pdf`);
  }

  /* allVehiclesDataAvailable(): boolean {
    return this.displayVehicles?.length > 0 && this.displayVehicles.some(v => v.dataAvailable);
  } */
}
